import type { QueryClient, QueryKey } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { filesKeys } from './filesKeys'
import type { AlmanacFileIndexItem, IndexPage, FilesInfiniteData } from './types'

const mapPagesIfChanged = (
  data: FilesInfiniteData,
  mapper: (page: IndexPage) => { next: IndexPage; changed: boolean },
): FilesInfiniteData | undefined => {
  let anyChanged = false
  const pages = data.pages.map((p) => {
    const { next, changed } = mapper(p)
    if (changed) anyChanged = true
    return next
  })
  return anyChanged ? { ...data, pages } : undefined
}

const getMatchingInfiniteFilesCaches = (queryClient: QueryClient, workspaceId?: number) => {
  if (!workspaceId) return [] as Array<[QueryKey, FilesInfiniteData | undefined]>

  // Get regular infinite queries (excludes calendar queries which are nested deeper)
  const infiniteQueries = queryClient.getQueriesData<FilesInfiniteData>({
    queryKey: filesKeys.partialInfinite(workspaceId),
  }) as Array<[QueryKey, FilesInfiniteData | undefined]>

  // Filter out calendar queries - they're handled separately
  return infiniteQueries.filter(([queryKey]) => {
    // Calendar keys have 'calendar' as the 4th element: ['files', workspaceId, 'infinite', 'calendar', ...]
    return queryKey[3] !== 'calendar'
  })
}

const getMatchingCalendarFilesCaches = (queryClient: QueryClient, workspaceId?: number) => {
  if (!workspaceId) return [] as Array<[QueryKey, AlmanacFileIndexItem[] | undefined]>

  // Get calendar queries which return flat arrays
  const calendarQueries = queryClient.getQueriesData<AlmanacFileIndexItem[]>({
    queryKey: filesKeys.partialCalendar(workspaceId),
  }) as Array<[QueryKey, AlmanacFileIndexItem[] | undefined]>

  return calendarQueries
}

export const optimisticUpdateFileInInfiniteLists = (
  queryClient: QueryClient,
  workspaceId: number | undefined,
  fileKey: string,
  updater: (f: AlmanacFileIndexItem) => AlmanacFileIndexItem,
) => {
  // Update regular infinite queries (with pages structure)
  const infiniteMatches = getMatchingInfiniteFilesCaches(queryClient, workspaceId)
  infiniteMatches.forEach(([queryKey, data]) => {
    if (!data) return
    const next = mapPagesIfChanged(data, (page) => {
      if (page.type !== SUCCESS_RESPONSE_TYPE) return { next: page, changed: false }

      let changed = false
      const nextFiles = page.data.almanac_files.map((f) => {
        if (f.key !== fileKey) return f
        const updated = updater(f)
        if (updated !== f) changed = true
        return updated
      })

      return changed
        ? { next: { ...page, data: { ...page.data, almanac_files: nextFiles } }, changed: true }
        : { next: page, changed: false }
    })
    if (next) queryClient.setQueryData<FilesInfiniteData>(queryKey, next)
  })

  // Update calendar queries (flat array structure)
  const calendarMatches = getMatchingCalendarFilesCaches(queryClient, workspaceId)
  calendarMatches.forEach(([queryKey, data]) => {
    if (!data || !Array.isArray(data)) return

    let changed = false
    const nextFiles = data.map((f) => {
      if (f.key !== fileKey) return f
      const updated = updater(f)
      if (updated !== f) changed = true
      return updated
    })

    if (changed) {
      queryClient.setQueryData<AlmanacFileIndexItem[]>(queryKey, nextFiles)
    }
  })
}

export const optimisticRemoveFilesFromInfiniteLists = (
  queryClient: QueryClient,
  workspaceId: number | undefined,
  fileKeys: string[],
) => {
  if (!workspaceId || fileKeys.length === 0) return

  const set = new Set(fileKeys)

  // Remove from regular infinite queries
  const infiniteMatches = getMatchingInfiniteFilesCaches(queryClient, workspaceId)
  infiniteMatches.forEach(([queryKey, data]) => {
    if (!data) return
    const next = mapPagesIfChanged(data, (page) => {
      if (page.type !== SUCCESS_RESPONSE_TYPE) return { next: page, changed: false }

      const filtered = page.data.almanac_files.filter((f) => !set.has(f.key))
      return filtered.length === page.data.almanac_files.length
        ? { next: page, changed: false }
        : {
            next: { ...page, data: { ...page.data, almanac_files: filtered } },
            changed: true,
          }
    })
    if (next) queryClient.setQueryData<FilesInfiniteData>(queryKey, next)
  })

  // Remove from calendar queries
  const calendarMatches = getMatchingCalendarFilesCaches(queryClient, workspaceId)
  calendarMatches.forEach(([queryKey, data]) => {
    if (!data || !Array.isArray(data)) return

    const filtered = data.filter((f) => !set.has(f.key))
    if (filtered.length !== data.length) {
      queryClient.setQueryData<AlmanacFileIndexItem[]>(queryKey, filtered)
    }
  })

  optimisticRemoveFromFavoritesCache(queryClient, workspaceId, fileKeys)
}

export const setFileDetailCache = <T extends { key: string }>(
  queryClient: QueryClient,
  workspaceId: number | undefined,
  file: T,
) => {
  if (!workspaceId) return
  const baseKey = filesKeys.detail(workspaceId, file.key)
  const matches = queryClient.getQueriesData<T>({ queryKey: baseKey })
  if (matches.length === 0) {
    queryClient.setQueryData(baseKey, file)
    return
  }
  matches.forEach(([queryKey]) => {
    queryClient.setQueryData(queryKey, file)
  })
}

export const updateFileDetailCache = <T extends { key: string }>(
  queryClient: QueryClient,
  workspaceId: number | undefined,
  fileKey: string,
  updater: (file: T) => T,
) => {
  if (!workspaceId) return
  const baseKey = filesKeys.detail(workspaceId, fileKey)
  const matches = queryClient.getQueriesData<T>({ queryKey: baseKey })
  matches.forEach(([queryKey, data]) => {
    if (!data) return
    const next = updater(data)
    if (next !== data) {
      queryClient.setQueryData(queryKey, next)
    }
  })
}

export const removeFileDetailCache = (queryClient: QueryClient, workspaceId: number | undefined, fileKey: string) => {
  if (!workspaceId) return
  queryClient.removeQueries({ queryKey: filesKeys.detail(workspaceId, fileKey) })
}

const favoritesQueryKey = (workspaceId: number) => ['workspace', workspaceId, 'files', 'favorites'] as const

/**
 * Optimistically removes favorites from the favorites cache by the almanac file's keys.
 * Matches favorites where favoritable_id equals the given fileKeys.
 */
export const optimisticRemoveFromFavoritesCache = (
  queryClient: QueryClient,
  workspaceId: number | undefined,
  fileKeys: string[],
) => {
  if (!workspaceId) return

  const queryKey = favoritesQueryKey(workspaceId)
  const data = queryClient.getQueryData<Array<{ favoritable_id: string }>>(queryKey)
  if (!data || !Array.isArray(data)) return

  const filtered = data.filter((f) => !fileKeys.includes(f.favoritable_id))
  if (filtered.length !== data.length) {
    queryClient.setQueryData(queryKey, filtered)
  }
}
